import type { Account, ParentRollup, RollupRow } from '@taler/api-client'
import {
  addMoney,
  type CurrencyCode,
  type Locale,
  type LocalDate,
  type Money,
  isZeroMoney,
  resolveLocalized,
  zeroMoney,
} from '@taler/domain'

export interface PeriodRollupLeafColumn {
  readonly kind: 'account' | 'other'
  readonly key: string
  readonly label: string
  /** The real account id - undefined for an 'other' leaf, which is not an account. */
  readonly accountId: string | undefined
  /** For an 'other' leaf: the expanded parent its stray postings belong to. */
  readonly parentAccountId: string | undefined
  readonly hasChildren: boolean
}

export interface PeriodRollupGroupColumn {
  readonly kind: 'group'
  readonly key: string
  readonly label: string
  readonly accountId: string
  readonly children: readonly PeriodRollupColumnNode[]
}

export type PeriodRollupColumnNode = PeriodRollupLeafColumn | PeriodRollupGroupColumn

export interface PeriodRollupHeaderCell {
  readonly key: string
  readonly label: string
  readonly colSpan: number
  readonly rowSpan: number
  readonly kind: PeriodRollupColumnNode['kind']
  readonly accountId: string | undefined
  /** Whether clicking this header toggles it - a group (already open) or a collapsed
   * parent; never a true leaf account or the synthetic "other" column. */
  readonly hasChildren: boolean
}

export interface PeriodRollupRow {
  readonly periodStart: LocalDate
  /** One cell per leaf column, in the same pre-order as the flattened leaves. */
  readonly cells: readonly Money[]
  readonly total: Money
}

export interface PeriodRollupTableData {
  readonly columnForest: readonly PeriodRollupColumnNode[]
  readonly headerRows: readonly (readonly PeriodRollupHeaderCell[])[]
  readonly rows: readonly PeriodRollupRow[]
  readonly leafCount: number
  /** Account columns shown, groups included; the synthetic "other" column is not an account. */
  readonly categoryCount: number
}

function leafCount(node: PeriodRollupColumnNode): number {
  return node.kind === 'group' ? node.children.reduce((sum, child) => sum + leafCount(child), 0) : 1
}

function forestDepth(nodes: readonly PeriodRollupColumnNode[]): number {
  let depth = 0
  for (const node of nodes) if (node.kind === 'group') depth = Math.max(depth, 1 + forestDepth(node.children))
  return depth
}

function buildHeaderRows(
  forest: readonly PeriodRollupColumnNode[],
  totalRows: number,
): PeriodRollupHeaderCell[][] {
  const rows: PeriodRollupHeaderCell[][] = Array.from({ length: totalRows }, () => [])
  const walk = (node: PeriodRollupColumnNode, level: number): void => {
    if (node.kind === 'group') {
      rows[level]!.push({
        key: node.key,
        label: node.label,
        colSpan: leafCount(node),
        rowSpan: 1,
        kind: 'group',
        accountId: node.accountId,
        hasChildren: true,
      })
      for (const child of node.children) walk(child, level + 1)
      return
    }
    rows[level]!.push({
      key: node.key,
      label: node.label,
      colSpan: 1,
      rowSpan: totalRows - level,
      kind: node.kind,
      accountId: node.accountId,
      hasChildren: node.hasChildren,
    })
  }
  for (const node of forest) walk(node, 0)
  return rows
}

function countAccounts(nodes: readonly PeriodRollupColumnNode[]): number {
  return nodes.reduce(
    (sum, node) =>
      sum + (node.kind === 'other' ? 0 : 1 + (node.kind === 'group' ? countAccounts(node.children) : 0)),
    0,
  )
}

function collectLeaves(nodes: readonly PeriodRollupColumnNode[]): PeriodRollupLeafColumn[] {
  const out: PeriodRollupLeafColumn[] = []
  for (const node of nodes) {
    if (node.kind === 'group') out.push(...collectLeaves(node.children))
    else out.push(node)
  }
  return out
}

interface ColumnTopology {
  readonly forest: readonly PeriodRollupColumnNode[]
  /** The account(s) with no parent - normally exactly one for EXPENSE/INCOME (a single
   * class-wide umbrella account), so its own rolled-up balance already *is* the period's
   * grand total. Kept as a list, summed with `addMoney` for the total, so a class that
   * ever grows a second top-level account still gets a correct (still server-sourced)
   * total instead of a silently wrong one. */
  readonly rootAccountIds: readonly string[]
}

/**
 * Pre-order over the class's account tree. A collapsed or childless account is one leaf
 * column carrying its rolled-up figure; an expanded parent becomes a group spanning its
 * children plus one trailing "other" leaf for its direct postings - the existing "Egyéb"
 * convention (`rollup-tree.ts`) generalised into a column. The root's own "other" leaf
 * appears only when it carries stray postings, since the root is never a visible parent. The
 * sole root of the class (there is exactly one umbrella account per class in this chart) is
 * always treated as open - collapsing the one and only branch to a single column would just
 * cost the reader an extra click for nothing - so the top-level columns are its children,
 * never the root itself. Topology (which accounts exist, who is whose parent) is identical
 * across every present period - the server always walks the full class tree - so one
 * period's rows are enough to build it.
 */
function buildColumnTopology(
  topologyRows: readonly RollupRow[],
  accountsById: ReadonlyMap<string, Account>,
  accountsWithOwnPostings: ReadonlySet<string>,
  liveAccountIds: ReadonlySet<string>,
  expanded: ReadonlySet<string>,
  locale: Locale,
): ColumnTopology {
  const childrenOf = new Map<string, string[]>()
  for (const row of topologyRows) {
    if (row.parentId === undefined) continue
    childrenOf.set(row.parentId, [...(childrenOf.get(row.parentId) ?? []), row.accountId])
  }
  const hasLiveDescendant = (accountId: string): boolean =>
    liveAccountIds.has(accountId) || (childrenOf.get(accountId) ?? []).some(hasLiveDescendant)
  const labelFor = (accountId: string): string => {
    const account = accountsById.get(accountId)
    return account === undefined ? accountId : (resolveLocalized(account.names, locale)?.name ?? account.code)
  }
  const byLabel = (a: string, b: string): number => labelFor(a).localeCompare(labelFor(b))

  const rootAccountIds = topologyRows
    .filter((row) => row.parentId === undefined)
    .map((row) => row.accountId)
    .sort(byLabel)
  const effectivelyExpanded =
    rootAccountIds.length === 1 ? new Set([...expanded, rootAccountIds[0]!]) : expanded

  const build = (accountId: string, isRoot: boolean): PeriodRollupColumnNode => {
    const children = (childrenOf.get(accountId) ?? []).filter(hasLiveDescendant).sort(byLabel)
    const hasChildren = children.length > 0
    if (!hasChildren || !effectivelyExpanded.has(accountId)) {
      return {
        kind: 'account',
        key: accountId,
        label: labelFor(accountId),
        accountId,
        parentAccountId: undefined,
        hasChildren,
      }
    }
    const childNodes = children.map((childId) => build(childId, false))
    const otherLeaves: PeriodRollupColumnNode[] =
      !isRoot || accountsWithOwnPostings.has(accountId)
        ? [
            {
              kind: 'other',
              key: `other:${accountId}`,
              label: '',
              accountId: undefined,
              parentAccountId: accountId,
              hasChildren: false,
            },
          ]
        : []
    return {
      kind: 'group',
      key: accountId,
      label: labelFor(accountId),
      accountId,
      children: [...childNodes, ...otherLeaves],
    }
  }

  if (rootAccountIds.length === 1) {
    const root = build(rootAccountIds[0]!, true)
    return { forest: root.kind === 'group' ? root.children : [root], rootAccountIds }
  }
  return { forest: rootAccountIds.filter(hasLiveDescendant).map((id) => build(id, true)), rootAccountIds }
}

export function buildPeriodRollupTable(
  rollup: ParentRollup,
  accountsById: ReadonlyMap<string, Account>,
  expanded: ReadonlySet<string>,
  locale: Locale,
  displayCurrency: CurrencyCode,
): PeriodRollupTableData {
  const periods = rollup.periods
  if (periods.length === 0) {
    return { columnForest: [], headerRows: [], rows: [], leafCount: 0, categoryCount: 0 }
  }

  // Columns that are zero in every period are not rendered.
  const liveAccountIds = new Set<string>()
  const accountsWithOwnPostings = new Set<string>()
  for (const period of periods) {
    for (const row of period.rows)
      if (!isZeroMoney(row.ownBalance) || !isZeroMoney(row.rolledUpBalance)) liveAccountIds.add(row.accountId)
    for (const [parentId, amount] of period.otherByParent)
      if (!isZeroMoney(amount)) {
        accountsWithOwnPostings.add(parentId)
        liveAccountIds.add(parentId)
      }
  }

  const { forest: columnForest, rootAccountIds } = buildColumnTopology(
    periods[0]!.rows,
    accountsById,
    accountsWithOwnPostings,
    liveAccountIds,
    expanded,
    locale,
  )
  const leaves = collectLeaves(columnForest)
  const headerRows = buildHeaderRows(columnForest, Math.max(forestDepth(columnForest) + 1, 2))

  const rows: PeriodRollupRow[] = periods.map((period) => {
    const rowsByAccountId = new Map(period.rows.map((row) => [row.accountId, row]))
    const cells = leaves.map((leaf) => {
      if (leaf.kind === 'other')
        return period.otherByParent.get(leaf.parentAccountId!) ?? zeroMoney(displayCurrency)
      return rowsByAccountId.get(leaf.accountId!)?.rolledUpBalance ?? zeroMoney(displayCurrency)
    })
    const rootBalances = rootAccountIds.map(
      (id) => rowsByAccountId.get(id)?.rolledUpBalance ?? zeroMoney(displayCurrency),
    )
    const total = rootBalances.length === 0 ? zeroMoney(displayCurrency) : rootBalances.reduce(addMoney)
    return { periodStart: period.periodStart, cells, total }
  })

  return {
    columnForest,
    headerRows,
    rows,
    leafCount: leaves.length,
    categoryCount: countAccounts(columnForest),
  }
}
